import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { LoginResponse, UserBusiness } from '@offconon/core-api';

import * as AuthActions from './auth.actions';
import { AuthEntity, AuthUser } from './auth.models';

export const AUTH_FEATURE_KEY = 'auth';

export interface AuthState extends EntityState<AuthEntity> {
  user?: AuthUser | null | undefined;
  clientId?: string;
  fcmId?: string;
  invitationCode?: string;
  error?: any;
  loading?: boolean;
  userBusinessEntity?: LoginResponse | UserBusiness;
}

export interface AuthPartialState {
  readonly [AUTH_FEATURE_KEY]: AuthState;
}

export const authAdapter: EntityAdapter<AuthEntity> = createEntityAdapter<AuthEntity>();

export const initialAuthState: AuthState = authAdapter.getInitialState({});

const reducer = createReducer(
  initialAuthState,
  on(AuthActions.initAuth, (state) => ({
    ...state,
    loaded: false,
    error: null,
    loading: false,
  })),

  on(AuthActions.loadAuthFailure, (state, { error }) => ({ ...state, error })),
  on(AuthActions.resetAuth, (state) => {
    return {
      ...state,
      user: undefined,
      userBusinessEntity: undefined,
    };
  }),

  on(AuthActions.setClientId, (state, { clientId }) => ({
    ...state,
    clientId: clientId,
  })),
  on(AuthActions.setFcmId, (state, { fcmId }) => ({ ...state, fcmId })),
  on(AuthActions.resetFcmId, (state) => {
    return {
      ...state,
      fcmId: undefined,
    };
  }),

  on(AuthActions.setInvitationCode, (state, { invitationCode }) => ({
    ...state,
    invitationCode: invitationCode,
  })),
  on(AuthActions.loginSuccess, (state, { loginResponse }) => ({ ...state, user: loginResponse })),
  on(AuthActions.authSetLoadingState, (state, { loading }) => {
    return { ...state, loading };
  }),

  on(AuthActions.refreshUserData, (state, { data }) => {
    if (!state.user) {
      return state;
    }

    const updatedUser = {
      ...state.user,
      ...data,
    };

    let updatedBusinessEntity = state.userBusinessEntity;
    if (isLoginResponse(state.userBusinessEntity)) {
      updatedBusinessEntity = {
        ...state.userBusinessEntity,
        ...updatedUser,
      };
    }

    return {
      ...state,
      user: updatedUser,
      userBusinessEntity: updatedBusinessEntity,
    };
  }),

  on(AuthActions.setBusinessEntity, (state, { business }) => ({
    ...state,
    userBusinessEntity: business,
  })),

  on(AuthActions.setUserEntity, (state) => {
    if (!state.user) return state;
    return {
      ...state,
      userBusinessEntity: {
        ...state.user,
      },
    };
  }),
  on(AuthActions.setChatSettings, (state, { settings }) => {
    if (!state.user) return state;
    return {
      ...state,
      user: {
        ...state.user,
        chat_settings: settings,
      },
    };
  }),
);

export function authReducer(state: AuthState | undefined, action: Action): AuthState {
  return reducer(state, action);
}

export function isUserBusiness(entity: unknown): entity is UserBusiness {
  return typeof entity === 'object' && entity !== null && 'business_id' in entity;
}

export function isLoginResponse(entity: unknown): entity is LoginResponse {
  return typeof entity === 'object' && entity !== null && 'username' in entity;
}
